/**
 * Lauf-Leiste (B12a, 28.10.2026): oben im Chat steht in EINEM Satz, was der Autonomie-Lauf gerade tut — „Läuft · noch 41 Min · letzter
 * Schritt: AWS-Seite geprüft“, „Wartet auf dich · Seite außerhalb des Rahmens“, „Abgelaufen um 07:47 – Wartezeit zählte mit“.
 *
 * Quelle: GET /api/lauf (server/lauf-ansicht.ts) — nur feste Kürzel und Zahlen, die Sätze kommen aus shared/lauf-ansicht.ts. Abgefragt alle
 * 5 s, solange die Seite sichtbar ist, und SOFORT beim Öffnen und Zurückkehren (Sichtbarkeit, pageshow) — so steht nach dem Wiederöffnen
 * gleich der aktuelle Stand da. 404 (Notschalter LAUF_ANSICHT=0, Kundenkonto): die Leiste bleibt verborgen, keine weitere Abfrage.
 *
 * ⚠️ Genau EINE Handlung: der Stopp-Knopf (vorhandene Route /api/chat/abort mit `{ stopp: true }`, sendeProzessStopp). Kein Ja, kein Weiter.
 *    28.10.2026: Ohne stoppbaren Lauf steht an seiner Stelle „Neuer Lauf“ (lib/lauf-start.ts) — er legt nur die Mandats-Karte in den Chat;
 *    das Mandat erteilt weiter allein Martins Tipp auf eine Stufe dort.
 * ⚠️ Texte nur über textContent. Keine Inline-Styles: Farbe über `data-stand`, der Zeitfaden ist ein <progress> (value/max als Attribute).
 * ⚠️ Barrierefreiheit: Bereich mit Beschriftung; der sichtbare Satz tickt (Restminuten) und ist darum NICHT live — eine eigene, unsichtbare
 *    Ansage sagt nur einen WECHSEL des Stands an (aria-live="polite"). Knopf 44 Punkte hoch, sichtbarer Fokus (CSS).
 */
import { sendeProzessStopp } from "./job-karte.js";
import { baueLaufStart, type LaufStartSenden } from "./lauf-start.js";
import { MANDAT_STUFEN_WERTE } from "../../shared/prozess-freigabe.js";
import {
  LAUF_ABFRAGE_MS,
  laufDetail,
  laufSatz,
  laufStoppbar,
  leseLaufBlick,
  uhrDe,
  type LaufBlick,
  type UhrFormat,
} from "../../shared/lauf-ansicht.js";

export const LAUF_LEISTE_ID = "lauf-leiste";
/** So lange bleibt der Stopp-Knopf nach einem angenommenen Stopp gesperrt, falls der Stand sich nicht ändert (ms). */
export const STOPP_SPERRE_MS = 15_000;

export type LaufHolen = () => Promise<{ status: number; daten: unknown }>;

export const holeLauf: LaufHolen = async () => {
  const r = await fetch("/api/lauf", { credentials: "same-origin", cache: "no-store" });
  return { status: r.status, daten: r.ok ? await r.json() : null };
};

export interface LaufLeiste {
  /** Stand neu holen (z. B. nach dem Stopp, beim Zurückkehren). */
  aktualisiere(): Promise<void>;
  /** Einen Stand direkt zeigen (Tests, Bilder). */
  zeige(b: LaufBlick): void;
  /** Zeitgeber und Hörer aufräumen. */
  stopp(): void;
}

export interface LaufLeisteOptionen {
  holen?: LaufHolen;
  stoppen?: () => Promise<boolean>;
  uhr?: UhrFormat;
  /** Abfrage-Takt starten (Standard ja). */
  abfragen?: boolean;
  /** „Neuen Lauf starten“ senden (Tests, Bilder). */
  starten?: LaufStartSenden;
}

/** Baut die Leiste VOR `vor` (dem Chat-Verlauf) in `wurzel` ein. Verborgen, bis der erste Stand da ist. */
export function baueLaufLeiste(wurzel: HTMLElement, vor: HTMLElement | null, o: LaufLeisteOptionen = {}): LaufLeiste {
  const holen = o.holen ?? holeLauf;
  const stoppen = o.stoppen ?? sendeProzessStopp;
  const uhr = o.uhr ?? uhrDe;
  const doc = wurzel.ownerDocument;
  const win = doc.defaultView;

  const leiste = doc.createElement("section");
  leiste.id = LAUF_LEISTE_ID;
  leiste.className = "lauf-leiste";
  leiste.hidden = true;
  leiste.setAttribute("aria-label", "Autonomer Lauf");

  const punkt = doc.createElement("span");
  punkt.className = "lauf-punkt";
  punkt.setAttribute("aria-hidden", "true");

  const text = doc.createElement("div");
  text.className = "lauf-text";
  const satz = doc.createElement("p");
  satz.className = "lauf-satz";
  const detail = doc.createElement("p");
  detail.className = "lauf-detail";
  text.append(satz, detail);

  const knopf = doc.createElement("button");
  knopf.type = "button";
  knopf.className = "lauf-stopp";
  knopf.textContent = "Stopp";
  knopf.setAttribute("aria-label", "Autonomen Lauf stoppen");

  const faden = doc.createElement("progress");
  faden.className = "lauf-faden";
  faden.setAttribute("aria-hidden", "true");

  const ansage = doc.createElement("p");
  ansage.className = "lauf-ansage";
  ansage.setAttribute("aria-live", "polite");

  const start = baueLaufStart(doc, o.starten);

  leiste.append(punkt, text, knopf, start.knopf, start.bereich, faden, ansage);
  wurzel.insertBefore(leiste, vor?.parentElement === wurzel ? vor : wurzel.firstChild);

  let letzter: LaufBlick | null = null;
  let takt: ReturnType<typeof setInterval> | null = null;
  let aus = false;
  let laeuft = false;
  let stoppt = false;
  let stopptMs = 0;

  const zeige = (b: LaufBlick): void => {
    const wechsel = letzter?.stand !== b.stand;
    letzter = b;
    leiste.hidden = false;
    leiste.dataset.stand = b.stand;
    const s = laufSatz(b, uhr);
    if (satz.textContent !== s) satz.textContent = s;
    const d = laufDetail(b, uhr);
    if (detail.textContent !== d) detail.textContent = d;
    detail.hidden = d === "";
    const stoppbar = laufStoppbar(b.stand);
    knopf.hidden = !stoppbar;
    start.erlaubt(!stoppbar);
    // Nach dem Stopp bleibt der Knopf gesperrt, bis der Stand wechselt — höchstens STOPP_SPERRE_MS (dann darf man noch einmal tippen).
    if (!stoppbar || !stoppt || Date.now() - stopptMs > STOPP_SPERRE_MS) {
      stoppt = false;
      knopf.disabled = false;
      knopf.textContent = "Stopp";
    }
    // Der Zeitfaden: wie viel vom Mandat noch übrig ist — nur mit Restzeit.
    if (b.restMin !== null && b.stufe) {
      const gesamt = Math.round(MANDAT_STUFEN_WERTE[b.stufe].ms / 60_000);
      faden.max = gesamt;
      faden.value = Math.min(gesamt, b.restMin);
      faden.hidden = false;
    } else {
      faden.hidden = true;
    }
    // Nur der WECHSEL wird angesagt (nicht jede Minute) — und nicht der erste Stand beim Laden.
    if (wechsel && ansage.dataset.bereit === "1") ansage.textContent = `Lauf: ${s}`;
    ansage.dataset.bereit = "1";
  };

  const verbergen = (): void => {
    leiste.hidden = true;
    start.erlaubt(false);
    letzter = null;
  };

  const aktualisiere = async (): Promise<void> => {
    if (aus || laeuft) return;
    laeuft = true;
    try {
      const r = await holen();
      if (r.status === 404) {
        // Notschalter oder Konto ohne Mandat: verborgen, keine weitere Abfrage.
        aus = true;
        halte();
        verbergen();
        return;
      }
      if (r.status !== 200) return; // 401/5xx: den letzten Stand stehen lassen, später wieder
      const b = leseLaufBlick(r.daten);
      if (b) zeige(b);
    } catch {
      /* Netz weg: der letzte Stand bleibt, die nächste Abfrage versucht es wieder */
    } finally {
      laeuft = false;
    }
  };

  knopf.addEventListener("click", () => {
    if (stoppt) return;
    stoppt = true;
    stopptMs = Date.now();
    knopf.disabled = true;
    knopf.textContent = "Stoppe …";
    void stoppen().then(
      (ok) => {
        if (!ok) return fehlgeschlagen();
        void aktualisiere();
      },
      () => fehlgeschlagen(),
    );
  });
  const fehlgeschlagen = (): void => {
    stoppt = false;
    knopf.disabled = false;
    knopf.textContent = "Stopp";
    detail.hidden = false;
    detail.textContent = "Das Stoppen hat nicht geklappt – tipp noch einmal auf Stopp.";
  };

  const sichtbar = (): boolean => doc.visibilityState !== "hidden";
  const starte = (): void => {
    if (takt || aus) return;
    takt = setInterval(() => {
      if (sichtbar()) void aktualisiere();
    }, LAUF_ABFRAGE_MS);
  };
  const halte = (): void => {
    if (takt) clearInterval(takt);
    takt = null;
  };
  const beimZurueckkehren = (): void => {
    if (sichtbar()) void aktualisiere();
  };

  if (o.abfragen !== false) {
    doc.addEventListener("visibilitychange", beimZurueckkehren);
    win?.addEventListener("pageshow", beimZurueckkehren);
    starte();
    void aktualisiere();
  }

  return {
    aktualisiere,
    zeige,
    stopp() {
      halte();
      doc.removeEventListener("visibilitychange", beimZurueckkehren);
      win?.removeEventListener("pageshow", beimZurueckkehren);
    },
  };
}
